import maplibregl, { type CustomLayerInterface } from 'maplibre-gl';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { Map as GeoboxMap } from '../Map';
import type { geoboxServer } from '../Types';
import {
  bufferUsesSpecularGlossiness,
  convertSpecularGlossinessToMetalRough,
} from './convertSpecularGlossiness';
import { ThreeboxBackend } from './ThreeboxBackend';
import {
  MAP_MODELS_CUSTOM_LAYER_ID,
  MAP_MODELS_LAYER_ID,
  MAP_MODELS_SELECTED_LAYER_ID,
  MAP_MODELS_SOURCE_ID,
  type MapPlacedModel,
  type MapModelsDocument,
  normalizeMapModelsDocument,
  toMapModelsDocument,
} from './types';

type LoadedMesh = {
  objUuid: string;
  scene: THREE.Object3D;
};

type ThreeRuntime = {
  scene?: THREE.Scene;
  camera?: THREE.Camera;
  renderer?: THREE.WebGLRenderer;
  modelsGroup?: THREE.Group;
  rootedMeshes: Map<string, THREE.Object3D>;
};

/**
 * Keep Three.js objects off any Vue-proxied instance fields.
 * Vue proxies break THREE read-only props like modelViewMatrix.
 */
const threeRuntimes = new WeakMap<object, ThreeRuntime>();

function unwrapMaybeProxy<T extends object>(value: T): T {
  const raw = (value as { __v_raw?: T }).__v_raw;
  return raw ?? value;
}

function getThreeRuntime(owner: object): ThreeRuntime {
  const key = unwrapMaybeProxy(owner);
  let runtime = threeRuntimes.get(key);
  if (!runtime) {
    runtime = { rootedMeshes: new Map() };
    threeRuntimes.set(key, runtime);
    // Also index by proxy identity if different, so lookups from either side work.
    if (key !== owner) threeRuntimes.set(owner, runtime);
  }
  return runtime;
}

/**
 * Placed 3D models on a Geobox map.
 *
 * Rendering follows the official MapLibre multi-model three.js pattern
 * (see "Adding 3D models using three.js on terrain"):
 * - Scene is ENU meters (east, up, north) via rotateX(π/2) + scale.z = -1
 * - Models are positioned in meters relative to a scene origin
 * - Each frame: camera.projectionMatrix = mapMatrix * originTransform
 * - GLB/GLTF loaded via GLTFLoader.parseAsync(ArrayBuffer)
 */
export class MapModelsManager {
  private map: GeoboxMap;
  private models: MapPlacedModel[] = [];
  private selectedId: string | null = null;
  private meshCache = new Map<string, LoadedMesh>();
  private loadingKeys = new Set<string>();
  private sceneDirty = true;
  private layerAdded = false;
  private loader = new GLTFLoader();
  private styleListenerBound = false;
  /** Optional threebox renderer; active only when healthy on this map. */
  private threebox: ThreeboxBackend | null = null;
  private threeboxTried = false;

  constructor(map: GeoboxMap) {
    this.map = map;
    getThreeRuntime(this);
  }

  private rt(): ThreeRuntime {
    return getThreeRuntime(this);
  }

  getModels(): MapPlacedModel[] {
    return this.models.map((m) => ({
      ...m,
      loc: [...m.loc] as [number, number, number],
      rotation: [...m.rotation] as [number, number, number],
    }));
  }

  getSelectedId(): string | null {
    return this.selectedId;
  }

  getSelected(): MapPlacedModel | null {
    return this.models.find((m) => m.id === this.selectedId) ?? null;
  }

  setModels(models: MapPlacedModel[]): void {
    this.models = models.map((m) => ({
      ...m,
      loc: [...m.loc] as [number, number, number],
      rotation: [...m.rotation] as [number, number, number],
    }));
    this.sceneDirty = true;
    void this.sync();
  }

  setFromDocument(doc: unknown): void {
    this.setModels(normalizeMapModelsDocument(doc));
  }

  toDocument(): MapModelsDocument {
    return toMapModelsDocument(this.models);
  }

  addModels(entries: MapPlacedModel[]): void {
    if (!entries.length) return;
    this.models = [...this.models, ...entries];
    this.sceneDirty = true;
    void this.sync();
  }

  updateModel(id: string, patch: Partial<MapPlacedModel>): void {
    this.models = this.models.map((model) =>
      model.id === id ? { ...model, ...patch, id: model.id } : model,
    );
    this.sceneDirty = true;
    void this.sync();
  }

  removeModel(id: string): void {
    this.models = this.models.filter((m) => m.id !== id);
    if (this.selectedId === id) this.selectedId = null;
    this.sceneDirty = true;
    void this.sync();
  }

  selectModel(id: string | null): void {
    this.selectedId = id;
    this.syncPickLayers();
    // Threebox renders the selection outline, so force a scene refresh + repaint.
    this.sceneDirty = true;
    this.map.triggerRepaint();
  }

  clear(): void {
    this.models = [];
    this.selectedId = null;
    this.sceneDirty = true;
    void this.sync();
  }

  zoomToModel(id: string): void {
    const model = this.models.find((m) => m.id === id);
    if (!model) return;
    this.selectModel(id);
    const currentZoom = this.map.getZoom();
    const targetZoom = Math.min(Math.max(currentZoom, 16), model.max_zoom || 22);
    this.map.flyTo({
      center: [model.loc[0], model.loc[1]],
      zoom: targetZoom,
      pitch: Math.max(this.map.getPitch(), 45),
      duration: 1200,
      essential: true,
    });
  }

  readModelIdFromFeature(
    feature: { properties?: Record<string, unknown> | null } | null | undefined,
  ): string | null {
    const id = feature?.properties?.__mapModelId;
    return typeof id === 'string' ? id : null;
  }

  /** Content URL for a catalog model uuid — always appends apikey when available. */
  resolveContentUrl(objUuid: string): string | null {
    if (!objUuid) return null;
    const server = this.map.server as geoboxServer & {
      _model3dEndpoint?: string;
      apikey?: string;
      origin?: string;
    };
    const query = this.buildAuthQuery(server);
    const endpoint = server._model3dEndpoint;
    if (endpoint) {
      return `${String(endpoint).replace(/\/?$/, '/')}${objUuid}/content${query}`;
    }
    const origin = server._origin || server.origin;
    if (origin) {
      return `${String(origin).replace(/\/?$/, '')}/v1/3dmodels/${objUuid}/content${query}`;
    }
    console.warn('[geobox-gl] map.server has no _origin/_model3dEndpoint; cannot build model content URL');
    return null;
  }

  private getApiKey(server: geoboxServer & { apikey?: string }): string {
    return String(server._apikey || server.apikey || '');
  }

  private buildAuthQuery(server: geoboxServer & { apikey?: string }): string {
    const key = this.getApiKey(server);
    return key ? `?apikey=${encodeURIComponent(key)}` : '';
  }

  async sync(): Promise<void> {
    this.bindStyleListener();

    if (!this.map.isStyleLoaded()) {
      this.map.once('idle', () => {
        void this.sync();
      });
      return;
    }

    try {
      this.syncPickLayers();
      await this.ensureMeshes();
      this.ensureCustomLayer();
      this.repositionAboveBasemap();
      this.sceneDirty = true;
      this.map.triggerRepaint();
    } catch (err) {
      // Style can flip mid-sync when openMap/setStyle races with idle.
      console.warn('[geobox-gl] map models sync deferred', err);
      this.map.once('idle', () => {
        void this.sync();
      });
    }
  }

  destroy(): void {
    if (this.styleListenerBound) {
      this.map.off('styledata', this.onStyleData);
      this.styleListenerBound = false;
    }
    try {
      if (this.map.getLayer(MAP_MODELS_CUSTOM_LAYER_ID)) {
        this.map.removeLayer(MAP_MODELS_CUSTOM_LAYER_ID);
      }
      if (this.map.getLayer(MAP_MODELS_SELECTED_LAYER_ID)) {
        this.map.removeLayer(MAP_MODELS_SELECTED_LAYER_ID);
      }
      if (this.map.getLayer(MAP_MODELS_LAYER_ID)) {
        this.map.removeLayer(MAP_MODELS_LAYER_ID);
      }
      if (this.map.getSource(MAP_MODELS_SOURCE_ID)) {
        this.map.removeSource(MAP_MODELS_SOURCE_ID);
      }
    } catch {
      /* ignore */
    }
    this.layerAdded = false;
    this.meshCache.clear();
    this.threebox?.dispose();
    this.threebox = null;
    this.threeboxTried = false;
    const rt = this.rt();
    rt.rootedMeshes.clear();
    rt.scene = undefined;
    rt.camera = undefined;
    rt.renderer = undefined;
    rt.modelsGroup = undefined;
  }

  private onStyleData = (): void => {
    // Only re-sync when our layers were wiped by a style swap — not on every addLayer.
    // Skip while a style is mid-load (Map.addSource would throw).
    if (!this.map.isStyleLoaded()) return;

    let customGone = true;
    let pickGone = true;
    try {
      customGone = !this.map.getLayer(MAP_MODELS_CUSTOM_LAYER_ID);
      pickGone = !this.map.getLayer(MAP_MODELS_LAYER_ID);
    } catch {
      return;
    }
    if (!customGone && !pickGone) {
      // Basemap swaps keep model layers but can stack rasters above the 3D custom layer.
      this.repositionAboveBasemap();
      return;
    }

    this.layerAdded = false;
    if (customGone) {
      const rt = this.rt();
      rt.scene = undefined;
      rt.camera = undefined;
      rt.renderer = undefined;
      rt.modelsGroup = undefined;
      rt.rootedMeshes.clear();
      // Custom layer (which hosts threebox) was wiped — re-probe on next add.
      this.threebox?.dispose();
      this.threebox = null;
      this.threeboxTried = false;
    }
    this.sceneDirty = true;
    void this.sync();
  };

  private bindStyleListener(): void {
    if (this.styleListenerBound) return;
    this.map.on('styledata', this.onStyleData);
    this.styleListenerBound = true;
  }

  private buildGeoJson(): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: this.models
        .filter((model) => model.visible !== false && !model.layer_bound && !model.layer_name)
        .map((model) => ({
          type: 'Feature',
          properties: {
            __mapModelId: model.id,
            __mapModelObj: model.obj,
            name: model.name,
          },
          geometry: {
            type: 'Point',
            coordinates: [model.loc[0], model.loc[1]],
          },
        })),
    };
  }

  private syncPickLayers(): void {
    try {
      const data = this.buildGeoJson();
      if (!this.map.getSource(MAP_MODELS_SOURCE_ID)) {
        this.map.addSource(MAP_MODELS_SOURCE_ID, {
          type: 'geojson',
          data,
        });
      } else {
        (this.map.getSource(MAP_MODELS_SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
      }

      // Invisible pick targets (keep for click/drag hit-testing; 3D mesh is the visual).
      if (!this.map.getLayer(MAP_MODELS_LAYER_ID)) {
        this.map.addLayer({
          id: MAP_MODELS_LAYER_ID,
          type: 'circle',
          source: MAP_MODELS_SOURCE_ID,
          paint: {
            'circle-radius': 14,
            'circle-color': '#000000',
            'circle-opacity': 0,
            'circle-stroke-width': 0,
            'circle-stroke-opacity': 0,
          },
        });
      } else {
        this.map.setPaintProperty(MAP_MODELS_LAYER_ID, 'circle-opacity', 0);
        this.map.setPaintProperty(MAP_MODELS_LAYER_ID, 'circle-stroke-opacity', 0);
        this.map.setPaintProperty(MAP_MODELS_LAYER_ID, 'circle-stroke-width', 0);
      }

      if (!this.map.getLayer(MAP_MODELS_SELECTED_LAYER_ID)) {
        this.map.addLayer({
          id: MAP_MODELS_SELECTED_LAYER_ID,
          type: 'circle',
          source: MAP_MODELS_SOURCE_ID,
          filter: ['==', ['get', '__mapModelId'], ''],
          paint: {
            'circle-radius': 16,
            'circle-color': '#000000',
            'circle-opacity': 0,
            'circle-stroke-width': 0,
            'circle-stroke-opacity': 0,
          },
        });
      } else {
        this.map.setPaintProperty(MAP_MODELS_SELECTED_LAYER_ID, 'circle-opacity', 0);
        this.map.setPaintProperty(MAP_MODELS_SELECTED_LAYER_ID, 'circle-stroke-opacity', 0);
        this.map.setPaintProperty(MAP_MODELS_SELECTED_LAYER_ID, 'circle-stroke-width', 0);
      }

      this.map.setFilter(MAP_MODELS_SELECTED_LAYER_ID, [
        '==',
        ['get', '__mapModelId'],
        this.selectedId ?? '',
      ]);
    } catch (err) {
      console.warn('[geobox-gl] map models pick layers failed', err);
    }
  }

  private async ensureMeshes(): Promise<void> {
    // Threebox downloads/caches its own GLBs — skip the native loader to avoid
    // fetching every model twice once threebox has taken over.
    if (this.threebox?.active) return;
    const unique = [...new Set(this.models.map((m) => m.obj).filter(Boolean))];
    await Promise.all(unique.map((uuid) => this.loadMesh(uuid)));
  }

  private async loadMesh(objUuid: string): Promise<void> {
    if (this.meshCache.has(objUuid) || this.loadingKeys.has(objUuid)) return;
    this.loadingKeys.add(objUuid);
    try {
      const contentUrl = this.resolveContentUrl(objUuid);
      if (!contentUrl) {
        console.warn('[geobox-gl] no content url for model', objUuid, {
          origin: (this.map.server as geoboxServer)?._origin,
          hasApikey: Boolean(this.getApiKey(this.map.server as geoboxServer & { apikey?: string })),
        });
        return;
      }

      console.info('[geobox-gl] loading model content', objUuid, contentUrl.split('?')[0]);
      let buffer = await this.fetchContentBuffer(contentUrl);

      // three.js r147+ dropped KHR_materials_pbrSpecularGlossiness — convert to metal/rough.
      if (bufferUsesSpecularGlossiness(buffer)) {
        console.info('[geobox-gl] converting specular-glossiness materials → metallic-roughness', objUuid);
        buffer = await convertSpecularGlossinessToMetalRough(buffer);
      }

      const gltf = await this.loader.parseAsync(buffer, '');
      this.prepareGltfScene(gltf.scene);
      this.meshCache.set(objUuid, { objUuid, scene: gltf.scene });
      this.sceneDirty = true;
      this.map.triggerRepaint();
    } catch (err) {
      console.warn('[geobox-gl] failed to load model content', objUuid, err);
    } finally {
      this.loadingKeys.delete(objUuid);
    }
  }

  /** Ensure materials/textures render correctly under MapLibre's shared WebGL context. */
  private prepareGltfScene(root: THREE.Object3D): void {
    root.updateMatrixWorld(true);
    root.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of materials) {
        if (!mat) continue;
        const std = mat as THREE.MeshStandardMaterial;
        // Keep double-sided if the asset asks for it; force update after conversion.
        std.needsUpdate = true;
        if ('map' in std && std.map) {
          std.map.colorSpace = THREE.SRGBColorSpace;
          std.map.needsUpdate = true;
        }
        if ('emissiveMap' in std && std.emissiveMap) {
          std.emissiveMap.colorSpace = THREE.SRGBColorSpace;
          std.emissiveMap.needsUpdate = true;
        }
      }
    });
  }

  private async fetchContentBuffer(url: string): Promise<ArrayBuffer> {
    const server = this.map.server as geoboxServer & { apikey?: string };
    let requestUrl = url;
    const key = this.getApiKey(server);

    if (!requestUrl.includes('apikey=') && key) {
      requestUrl += `${requestUrl.includes('?') ? '&' : '?'}apikey=${encodeURIComponent(key)}`;
    }

    const headers: Record<string, string> = {};
    if (!requestUrl.includes('apikey=') && server._access_token) {
      headers.Authorization = `Bearer ${server._access_token}`;
    }

    const response = await fetch(requestUrl, { headers });
    if (!response.ok) {
      throw new Error(`Model content download failed (${response.status}): ${requestUrl}`);
    }

    const buffer = await response.arrayBuffer();
    const magic = new TextDecoder().decode(buffer.slice(0, 4));
    if (magic !== 'glTF' && magic !== '{"' && magic !== '{\n"' && !magic.startsWith('{')) {
      // Still try parse — some servers wrap content; GLTFLoader will error clearly.
      console.warn('[geobox-gl] unexpected model content header', magic, requestUrl);
    }
    return buffer;
  }

  private ensureCustomLayer(): void {
    if (this.map.getLayer(MAP_MODELS_CUSTOM_LAYER_ID)) {
      this.layerAdded = true;
      this.repositionAboveBasemap();
      return;
    }

    try {
      // Insert under pick circles so markers stay clickable on top.
      const beforeId = this.map.getLayer(MAP_MODELS_LAYER_ID)
        ? MAP_MODELS_LAYER_ID
        : undefined;
      this.map.addLayer(this.createCustomLayer(), beforeId);
      this.layerAdded = true;
      this.repositionAboveBasemap();
    } catch (err) {
      console.warn('[geobox-gl] failed to add 3d models custom layer', err);
    }
  }

  /**
   * Keep the 3D custom layer above basemap rasters but below invisible pick handles.
   * MapLibre paints layers in style order — a raster above the custom layer covers models.
   */
  repositionAboveBasemap(): void {
    if (!this.map.isStyleLoaded()) return;
    if (!this.map.getLayer(MAP_MODELS_CUSTOM_LAYER_ID)) return;

    const basemapId = this.map.getLayer('geobox-gl-custom-base-map')
      ? 'geobox-gl-custom-base-map'
      : this.map.getLayer('geobox-gl-base-map')
        ? 'geobox-gl-base-map'
        : null;

    const pickAnchor = this.map.getLayer(MAP_MODELS_LAYER_ID)
      ? MAP_MODELS_LAYER_ID
      : this.map.getLayer(MAP_MODELS_SELECTED_LAYER_ID)
        ? MAP_MODELS_SELECTED_LAYER_ID
        : undefined;

    try {
      if (pickAnchor) {
        this.map.moveLayer(MAP_MODELS_CUSTOM_LAYER_ID, pickAnchor);
      } else {
        this.map.moveLayer(MAP_MODELS_CUSTOM_LAYER_ID);
      }

      if (basemapId) {
        this.map.moveLayer(basemapId, MAP_MODELS_CUSTOM_LAYER_ID);
      }

      if (
        this.map.getLayer(MAP_MODELS_SELECTED_LAYER_ID)
        && this.map.getLayer(MAP_MODELS_LAYER_ID)
      ) {
        this.map.moveLayer(MAP_MODELS_SELECTED_LAYER_ID);
      }
    } catch {
      /* ignore transient moveLayer races during style mutations */
    }
  }

  /** Meters east/north/up of `loc` relative to scene origin lng/lat/alt. */
  private offsetMeters(
    origin: maplibregl.MercatorCoordinate,
    loc: [number, number, number],
  ): { east: number; north: number; up: number } {
    const target = maplibregl.MercatorCoordinate.fromLngLat(
      { lng: loc[0], lat: loc[1] },
      loc[2] ?? 0,
    );
    const metersPerMercator = origin.meterInMercatorCoordinateUnits();
    return {
      east: (target.x - origin.x) / metersPerMercator,
      // mercator y grows south → flip for north-positive ENU
      north: (origin.y - target.y) / metersPerMercator,
      up: (target.z - origin.z) / metersPerMercator,
    };
  }

  private rebuildSceneObjects(): void {
    const rt = this.rt();
    if (!rt.modelsGroup) return;

    for (const node of rt.rootedMeshes.values()) {
      rt.modelsGroup.remove(node);
    }
    rt.rootedMeshes.clear();

    const zoom = this.map.getZoom();
    for (const model of this.models) {
      if (model.visible === false) continue;
      // Binding stubs are persistence metadata only — never mesh them.
      if (model.layer_bound || model.layer_name) continue;
      if (zoom < model.min_zoom || zoom > model.max_zoom) continue;
      const mesh = this.meshCache.get(model.obj);
      if (!mesh) continue;

      const root = new THREE.Group();
      root.name = model.id;
      const clone = mesh.scene.clone(true);
      root.add(clone);

      // User rotation is applied in model/ENU space (radians).
      root.rotation.set(model.rotation[0] ?? 0, model.rotation[1] ?? 0, model.rotation[2] ?? 0);
      const s = Math.max(model.scale ?? 1, 0.01);
      root.scale.set(s, s, s);
      root.visible = true;

      rt.modelsGroup.add(root);
      rt.rootedMeshes.set(model.id, root);
    }

    this.sceneDirty = false;
  }

  private updateModelPositions(originMercator: maplibregl.MercatorCoordinate): void {
    const rt = this.rt();
    for (const model of this.models) {
      const node = rt.rootedMeshes.get(model.id);
      if (!node) continue;
      const { east, north, up } = this.offsetMeters(originMercator, model.loc);
      // Scene is ENU: x=east, y=up, z=north
      node.position.set(east, up, north);
      node.rotation.set(model.rotation[0] ?? 0, model.rotation[1] ?? 0, model.rotation[2] ?? 0);
      const s = Math.max(model.scale ?? 1, 0.01);
      node.scale.set(s, s, s);
      node.visible = model.visible !== false;
    }
  }

  private readMainMatrix(args: unknown): ArrayLike<number> | null {
    if (!args || typeof args !== 'object') {
      if (Array.isArray(args) || ArrayBuffer.isView(args)) return args as ArrayLike<number>;
      return null;
    }
    const typed = args as {
      defaultProjectionData?: { mainMatrix?: ArrayLike<number> };
      getMatrixForModel?: (lngLat: [number, number], altitude?: number) => number[];
    };
    const main = typed.defaultProjectionData?.mainMatrix;
    if (Array.isArray(main) || ArrayBuffer.isView(main)) {
      return main as ArrayLike<number>;
    }
    return null;
  }

  private createCustomLayer(): CustomLayerInterface {
    const manager = this;
    return {
      id: MAP_MODELS_CUSTOM_LAYER_ID,
      type: 'custom',
      renderingMode: '3d',
      onAdd(_map, gl) {
        const rt = manager.rt();
        rt.camera = new THREE.Camera();
        rt.scene = new THREE.Scene();

        // Official MapLibre ENU setup: three.js Y-up → map up, Z toward viewer → north.
        rt.scene.rotateX(Math.PI / 2);
        rt.scene.scale.multiply(new THREE.Vector3(1, 1, -1));

        const light1 = new THREE.DirectionalLight(0xffffff, 1.0);
        light1.position.set(50, 70, -30).normalize();
        rt.scene.add(light1);

        const light2 = new THREE.DirectionalLight(0xffffff, 0.4);
        light2.position.set(-30, 40, 20).normalize();
        rt.scene.add(light2);

        rt.scene.add(new THREE.AmbientLight(0xffffff, 0.55));

        rt.modelsGroup = new THREE.Group();
        rt.scene.add(rt.modelsGroup);
        rt.rootedMeshes = new Map();
        manager.sceneDirty = true;

        rt.renderer = new THREE.WebGLRenderer({
          canvas: manager.map.getCanvas(),
          context: gl as WebGLRenderingContext,
          antialias: true,
        });
        rt.renderer.autoClear = false;

        // Attempt the threebox backend once; it self-disables (falls back to
        // this native renderer) if it can't produce a valid camera on MapLibre.
        if (!manager.threeboxTried) {
          manager.threeboxTried = true;
          const backend = new ThreeboxBackend({
            map: manager.map,
            resolveContentUrl: (uuid) => manager.resolveContentUrl(uuid),
          });
          if (backend.init(gl as WebGLRenderingContext)) {
            manager.threebox = backend;
            manager.sceneDirty = true;
          }
        }
      },
      render(_gl, args) {
        try {
          // Threebox path: it owns camera sync + rendering; keep our objects in
          // sync and let threebox draw above the basemap.
          if (manager.threebox?.active) {
            if (manager.sceneDirty || manager.threebox.needsResync()) {
              manager.threebox.sync(manager.models, manager.selectedId);
              manager.sceneDirty = false;
            }
            manager.threebox.render();
            return;
          }

          const rt = manager.rt();
          if (!rt.camera || !rt.scene || !rt.renderer || !rt.modelsGroup) {
            return;
          }

          if (manager.sceneDirty) {
            manager.rebuildSceneObjects();
          } else if (rt.rootedMeshes.size === 0 && manager.models.length > 0) {
            // Meshes may have finished loading after last rebuild.
            manager.rebuildSceneObjects();
          }

          if (rt.rootedMeshes.size === 0) return;

          // Scene origin: first placed model (stable) keeps meter offsets small.
          const anchor = manager.models[0];
          if (!anchor) return;

          const altitude = anchor.loc[2] ?? 0;
          const originMercator = maplibregl.MercatorCoordinate.fromLngLat(
            { lng: anchor.loc[0], lat: anchor.loc[1] },
            altitude,
          );

          manager.updateModelPositions(originMercator);

          const mainMatrix = manager.readMainMatrix(args);
          if (!mainMatrix) {
            console.warn('[geobox-gl] missing defaultProjectionData.mainMatrix');
            return;
          }

          const meterScale = originMercator.meterInMercatorCoordinateUnits();
          const m = new THREE.Matrix4().fromArray(mainMatrix as number[]);
          const l = new THREE.Matrix4()
            .makeTranslation(originMercator.x, originMercator.y, originMercator.z)
            .scale(new THREE.Vector3(meterScale, -meterScale, meterScale));

          // Official MapLibre three.js contract:
          rt.camera.projectionMatrix = m.multiply(l);
          rt.renderer.resetState();
          rt.renderer.render(rt.scene, rt.camera);
        } catch (err) {
          console.warn('[geobox-gl] 3d models render failed', err);
        }
      },
    };
  }
}
